import { Stack } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";

import { useCurrentUser } from "Auth/Hooks/useCurrentUser";
import { AccessRoleCode } from "Infrastructure/Api/Api";
import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import type { PartnerPrefillData } from "Partners/Forms/PartnerForm";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";
import { NewPartnerButton } from "../Components/NewPartnerButton";
import { PartnersTable } from "../Components/PartnersTable";

export const PartnersPage = (): React.ReactNode => {
  const { t } = useResource();
  const navigate = useAppNavigate();
  const location = useLocation();
  const { isLoading, hasRole, isRootPartner, user } = useCurrentUser();

  useEffect(() => {
    if (!isLoading && hasRole(AccessRoleCode.Partner) && !isRootPartner) {
      navigate(AppRouting.Dashboard.path());
    }
  }, [isLoading, hasRole, isRootPartner, navigate]);

  const prefillPartner = location.state?.prefillPartner as
    | PartnerPrefillData
    | undefined;

  const autoOpen = !!prefillPartner;

  // Clear navigation state after consuming it
  useEffect(() => {
    if (prefillPartner) {
      window.history.replaceState({}, "");
    }
  }, [prefillPartner]);

  const parentPartnerID = isRootPartner
    ? (user?.partnerID ?? undefined)
    : undefined;

  const actionButton = useMemo(
    () => (
      <NewPartnerButton
        parentPartnerID={parentPartnerID}
        prefill={prefillPartner}
        autoOpen={autoOpen}
      />
    ),
    [parentPartnerID, prefillPartner, autoOpen],
  );

  return (
    <AuthenticatedLayout
      preTitle={t(Resources.Common.Admin)}
      title={t(Resources.Common.Partners)}
      action={actionButton}
    >
      <Stack spacing={Spacing.L}>
        <PartnersTable />
      </Stack>
    </AuthenticatedLayout>
  );
};
